import React, { useEffect, useState } from 'react';
import { Paper, Typography, Box, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, CircularProgress, Button, Dialog, DialogTitle, DialogContent, DialogActions, TextField } from '@mui/material';
import axios from 'axios';

const LANDLORD_API_BASE_URL = 'https://bunnyrent-backend-917484933271.us-central1.run.app/api';
const landlordApi = axios.create({
  baseURL: LANDLORD_API_BASE_URL,
  headers: { 'Content-Type': 'application/json' },
});
landlordApi.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('landlord_access_token') || localStorage.getItem('access_token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

export default function LandlordContacts() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [signedDialog, setSignedDialog] = useState({ open: false, idx: null, progressId: null, moveInDate: '' });
  const [declineDialog, setDeclineDialog] = useState({ open: false, idx: null, progressId: null });

  const loadData = () => {
    setLoading(true);
    landlordApi.get('/properties/progress/landlord/contacted/')
      .then(res => {
        setRows(res.data);
        setLoading(false);
      })
      .catch(err => {
        setError('Failed to load');
        setLoading(false);
      });
  };

  useEffect(() => { loadData(); }, []);

  const handleMarkSigned = async () => {
    const { idx, progressId, moveInDate } = signedDialog;
    try {
      await landlordApi.patch(`/properties/progress/${progressId}/landlord-update-status/`, {
        status: 'signed',
        move_in_date: moveInDate || null,
      });
      setRows(prev => prev.filter((_, i) => i !== idx));
      setSignedDialog({ open: false, idx: null, progressId: null, moveInDate: '' });
    } catch (e) {
      alert('Failed to mark signed.');
    }
  };

  const handleDecline = async () => {
    const { idx, progressId } = declineDialog;
    try {
      await landlordApi.patch(`/properties/progress/${progressId}/landlord-update-status/`, { status: 'declined' });
      setRows(prev => prev.filter((_, i) => i !== idx));
      setDeclineDialog({ open: false, idx: null, progressId: null });
    } catch (e) {
      alert('Failed to decline.');
    }
  };

  return (
    <Paper elevation={3} sx={{ p: 4, borderRadius: 3, bgcolor: 'background.paper', mt: 4 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" sx={{ color: 'primary.main', fontWeight: 700 }}>
          Contacts (Awaiting Decision)
        </Typography>
      </Box>
      <Box sx={{ mt: 3 }}>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 200 }}>
            <CircularProgress />
          </Box>
        ) : error ? (
          <Typography color="error">{error}</Typography>
        ) : rows.length === 0 ? (
          <Typography color="text.secondary">No contacted students</Typography>
        ) : (
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Student Email</TableCell>
                  <TableCell>Student Name</TableCell>
                  <TableCell>Property</TableCell>
                  <TableCell>Address</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.map((row, idx) => (
                  <TableRow key={row.id}>
                    <TableCell>{row.user_email}</TableCell>
                    <TableCell>{row.user_full_name}</TableCell>
                    <TableCell>{row.rental_title}</TableCell>
                    <TableCell>{row.rental_address}</TableCell>
                    <TableCell>{row.status}</TableCell>
                    <TableCell align="right">
                      <Button size="small" variant="contained" sx={{ mr: 1 }} onClick={() => setSignedDialog({ open: true, idx, progressId: row.id, moveInDate: row.move_in_date || '' })}>Mark Signed</Button>
                      <Button size="small" variant="outlined" color="error" onClick={() => setDeclineDialog({ open: true, idx, progressId: row.id })}>Decline</Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Box>

      {/* Mark Signed Dialog */}
      <Dialog open={signedDialog.open} onClose={() => setSignedDialog({ open: false, idx: null, progressId: null, moveInDate: '' })}>
        <DialogTitle>Mark as Signed</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 2 }}>Optionally set a move-in date:</Typography>
          <TextField
            label="Move-in Date"
            type="date"
            value={signedDialog.moveInDate || ''}
            onChange={(e) => setSignedDialog(prev => ({ ...prev, moveInDate: e.target.value }))}
            InputLabelProps={{ shrink: true }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSignedDialog({ open: false, idx: null, progressId: null, moveInDate: '' })}>Cancel</Button>
          <Button variant="contained" onClick={handleMarkSigned}>Confirm</Button>
        </DialogActions>
      </Dialog>

      {/* Decline Dialog */}
      <Dialog open={declineDialog.open} onClose={() => setDeclineDialog({ open: false, idx: null, progressId: null })}>
        <DialogTitle>Decline</DialogTitle>
        <DialogContent>Are you sure you want to mark this contact as declined?</DialogContent>
        <DialogActions>
          <Button onClick={() => setDeclineDialog({ open: false, idx: null, progressId: null })}>Cancel</Button>
          <Button variant="contained" color="error" onClick={handleDecline}>Decline</Button>
        </DialogActions>
      </Dialog>
    </Paper>
  );
} 